import { useEffect } from 'react'
import { useAutopilotStore } from '../../autopilot/store/autopilot-store'
import type { PlatformConnectionState } from '../hooks/usePlatformConnections'
import type { LivePlatform, PostIdea } from '../store/idea-store'

/**
 * Default publish-set for a fresh Post (nothing staged or confirmed yet).
 * Precedence: the active autopilot plan's wizard channels (explicit user
 * intent) → connected channels → nothing; the primary platform is ALWAYS
 * included — it anchors the post (ComposerShell contract: the active tab
 * must be in the selected set) and it's the content the user is looking at,
 * so excluding it would schedule variants they never reviewed. Everything is
 * intersected with the shape's candidate platforms, and no unconnected
 * platform beyond the primary is ever pre-selected — connect-on-submit still
 * lets the user add one manually.
 * See playbook/product/multi-platform/DESIGN.md (Phase 0).
 */
export function defaultPlatformSeed(
  idea: Pick<PostIdea, 'candidatePlatforms' | 'primaryPlatform'>,
  connections: Record<LivePlatform, PlatformConnectionState>
): LivePlatform[] {
  const connected = idea.candidatePlatforms.filter(p => connections[p]?.connected)
  const planChannels = readPlanChannels()
  const fromPlan = connected.filter(p => planChannels.has(p))
  const base = fromPlan.length > 0 ? fromPlan : connected
  return base.includes(idea.primaryPlatform) ? base : [idea.primaryPlatform, ...base]
}

/**
 * Wizard channels of the first ACTIVE plan. Read-only getState peek; loading
 * is the caller's concern (see useEnsureAutopilotPlansLoaded) so the
 * plan-channels rule applies deterministically on every entry path, not just
 * when a Home component happened to fetch plans first.
 */
function readPlanChannels(): Set<string> {
  const { autopilots, loaded } = useAutopilotStore.getState()
  if (!loaded) return new Set()
  for (const plan of autopilots) {
    // fetchAutopilots only loads active plans, but a plan paused THIS session
    // stays in the array with its status flipped in place — skip it.
    if (plan.status !== 'active') continue
    const raw = plan.contentSpecifics?.channels
    if (Array.isArray(raw) && raw.length > 0) return new Set(raw as string[])
    if (plan.channel) return new Set([plan.channel])
  }
  return new Set()
}

/**
 * Ensures the autopilot store is loaded (kicking a fetch at most once) and
 * returns its loaded flag. Seeding gates on this so the plan-channels
 * precedence is deterministic on cold deep-links, not an accident of which
 * surface rendered first. fetchAutopilots sets loaded=true even on failure,
 * so this can't gate forever.
 */
export function useEnsureAutopilotPlansLoaded(): boolean {
  const loaded = useAutopilotStore(s => s.loaded)
  useEffect(() => {
    if (!loaded) void useAutopilotStore.getState().fetchAutopilots()
  }, [loaded])
  return loaded
}
